import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import NotificationContainer from './NotificationSystem';
import apiClient from '../services/unifiedApiClient';
import { tokens } from '../design-system';
import { HelpCenterButton, HelpCenterPanel } from './help';
import SystemBanner from './SystemBanner';
import PublicNav from './PublicNav';

// ─── Icons ───────────────────────────────────────────────────────────────────

const HomeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 11l9-8 9 8v10a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1z"/>
  </svg>
);
const IdeaIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z"/>
  </svg>
);
const BookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h12a4 4 0 0 1 4 4v12H8a4 4 0 0 1-4-4z"/><path d="M4 16a4 4 0 0 1 4-4h12"/>
  </svg>
);
const SettingsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M12 5v14M5 12h14"/>
  </svg>
);
const LogoutIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
  </svg>
);

// ─── Shared tokens ───────────────────────────────────────────────────────────

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#eedfbf',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// ─── Sidebar nav item ────────────────────────────────────────────────────────

const SideLink = ({ icon, label, count, active, to, onClick, badge }) => {
  const navigate = useNavigate();
  const handleClick = () => {
    if (onClick) onClick();
    if (to) navigate(to);
  };
  return (
    <div
      onClick={handleClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 12px',
        margin: '1px 0',
        borderRadius: T.r3,
        fontSize: 14, fontWeight: 600,
        color: active ? T.ink : T.ink2,
        background: active ? T.card : 'transparent',
        border: active ? `1px solid ${T.rule}` : '1px solid transparent',
        boxShadow: active ? T.sh1 : 'none',
        textDecoration: 'none',
        cursor: 'pointer',
        fontFamily: T.sans,
        userSelect: 'none',
      }}
    >
      <span style={{ color: active ? T.orange : T.inkMuted, display: 'inline-flex', flexShrink: 0 }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {count != null && (
        <span style={{
          fontFamily: T.mono, fontSize: 11, color: T.inkMuted,
          background: T.paperDeep, padding: '1px 7px', borderRadius: T.full,
        }}>{count}</span>
      )}
      {badge && badge}
    </div>
  );
};

const Eyebrow = ({ children }) => (
  <div style={{
    fontSize: 11, fontWeight: 800, letterSpacing: 0.8,
    color: T.inkMuted, textTransform: 'uppercase',
    padding: '0 12px 8px',
    fontFamily: T.sans,
  }}>{children}</div>
);

// ─── Wordmark ────────────────────────────────────────────────────────────────

const Wordmark = ({ size = 24 }) => (
  <img
    src="/text-logo.png"
    alt="Cahoots"
    style={{ height: size, objectFit: 'contain', display: 'block' }}
  />
);

// ─── Sidebar shell (authenticated layout) ───────────────────────────────────

const SidebarLayout = ({ children, user, unreadBlogCount }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { isEnterprise } = useSubscription();
  const { globalLoading } = useApp();

  // Close drawer on route change
  useEffect(() => {
    // reserved for future mobile drawer
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (e) {
      console.error('Logout error:', e);
    }
  };

  const isAt = (path) => {
    if (path === '/ideas') return location.pathname === '/ideas' || location.pathname.startsWith('/ideas/');
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Subscription display
  const tier = user?.subscription_tier || 'free';
  const tierLabel = tier === 'free' ? 'Free' : tier === 'hobbyist' ? 'Hobbyist' : tier === 'pro' ? 'Pro' : 'Enterprise';
  const initials = user?.full_name
    ? user.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() || '?';

  const Sidebar = () => (
    <aside style={{
      width: 232,
      minWidth: 232,
      background: T.paperDeep,
      borderRight: `1px dashed ${T.ruleStrong}`,
      padding: '22px 14px',
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      position: 'sticky',
      top: 0,
      overflowY: 'auto',
    }}>
      {/* Logo */}
      <div style={{ padding: '2px 10px 20px' }}>
        <Link to="/ideas" style={{ display: 'inline-block' }}>
          <Wordmark size={24} />
        </Link>
      </div>

      {/* Workspace nav */}
      <Eyebrow>Workspace</Eyebrow>
      <SideLink icon={<HomeIcon />} label="Dashboard" to="/dashboard" active={location.pathname === '/dashboard'} />
      <SideLink icon={<IdeaIcon />} label="Ideas" to="/ideas" active={isAt('/ideas')} />

      <div style={{ height: 16 }} />

      {/* Library nav */}
      <Eyebrow>Library</Eyebrow>
      <SideLink
        icon={<BookIcon />}
        label="Blog"
        to="/blog"
        active={location.pathname.startsWith('/blog') && !location.pathname.startsWith('/blog/admin')}
        badge={unreadBlogCount > 0 && (
          <span style={{
            background: T.capRed, color: '#fff',
            borderRadius: T.full, padding: '0 6px',
            fontSize: 10, fontWeight: 800,
          }}>{unreadBlogCount > 9 ? '9+' : unreadBlogCount}</span>
        )}
      />
      <SideLink icon={<SettingsIcon />} label="Settings" to="/settings" active={location.pathname === '/settings'} />
      {user?.role === 'admin' && (
        <SideLink icon={<BookIcon />} label="Blog Admin" to="/blog/admin" active={location.pathname === '/blog/admin'} />
      )}

      {/* Spacer */}
      <div style={{ marginTop: 'auto' }} />

      {/* New idea + Help */}
      <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
        <div
          onClick={() => navigate('/ideas/create')}
          style={{
            flex: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '10px 14px',
            background: T.orange, color: '#fff',
            borderRadius: T.r3, cursor: 'pointer',
            fontSize: 14, fontWeight: 700,
            fontFamily: T.sans,
            boxShadow: '0 2px 8px rgba(234,124,31,0.3)',
          }}
        >
          <PlusIcon /> New idea
        </div>
        <HelpCenterButton />
      </div>

      {/* User card */}
      <div style={{
        background: T.card,
        border: `1px solid ${T.rule}`,
        borderRadius: T.r3,
        padding: 14,
        boxShadow: T.sh1,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Avatar */}
          <div style={{
            width: 32, height: 32, borderRadius: T.full,
            background: T.moss, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 800, flexShrink: 0,
            fontFamily: T.sans,
          }}>{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 13, fontWeight: 700, color: T.ink,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              fontFamily: T.sans,
            }}>{user?.full_name || user?.email || 'Account'}</div>
            <div style={{
              fontFamily: T.mono, fontSize: 10, color: T.inkMuted,
            }}>{tierLabel}</div>
          </div>
        </div>

        {/* Logout */}
        <div
          onClick={handleLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            marginTop: 10, padding: '6px 10px',
            border: `1px solid ${T.rule}`,
            borderRadius: T.r2, cursor: 'pointer',
            fontSize: 12, fontWeight: 600, color: T.inkMuted,
            fontFamily: T.sans,
          }}
        >
          <LogoutIcon /> Sign out
        </div>

        {/* Upgrade nudge */}
        {!isEnterprise && tier === 'free' && (
          <div
            onClick={() => navigate('/pricing')}
            style={{
              marginTop: 8, padding: '6px 10px',
              background: `${T.orange}15`,
              border: `1px solid ${T.orange}40`,
              borderRadius: T.r2, cursor: 'pointer',
              fontSize: 12, fontWeight: 700, color: T.orangeDark,
              textAlign: 'center', fontFamily: T.sans,
            }}
          >
            Upgrade to Pro
          </div>
        )}
      </div>
    </aside>
  );

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: T.paper,
      fontFamily: T.sans,
    }}>
      <Sidebar />

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflowX: 'hidden' }}>
        <SystemBanner />
        <main style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
          {children}
        </main>
      </div>

      {/* Global loading overlay */}
      {globalLoading && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(247,239,216,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: tokens.zIndex.overlay,
        }}>
          <div style={{
            width: 48, height: 48,
            border: `4px solid ${T.rule}`,
            borderTop: `4px solid ${T.orange}`,
            borderRadius: T.full,
            animation: 'spin 1s linear infinite',
          }} />
        </div>
      )}

      <NotificationContainer />
      <HelpCenterPanel />
    </div>
  );
};


// ─── Main Layout component ────────────────────────────────────────────────────

const Layout = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [unreadBlogCount, setUnreadBlogCount] = useState(0);

  // Fetch unread blog count for authenticated users
  useEffect(() => {
    if (!isAuthenticated()) return;
    const fetchCount = async () => {
      try {
        const res = await apiClient.get('/blog/unread-count');
        setUnreadBlogCount(res.unread_count || 0);
      } catch { /* silent */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Mark blog as read when on blog page
  useEffect(() => {
    if (!isAuthenticated() || !location.pathname.startsWith('/blog')) return;
    apiClient.post('/blog/mark-read').then(() => setUnreadBlogCount(0)).catch(() => {});
  }, [location.pathname, isAuthenticated]);

  // Routes where we render NO layout wrapper — the page handles everything
  const noWrapperRoutes = ['/', '/landing', '/oauth-callback', '/checkout/return'];
  const isOAuthCallback = location.pathname.startsWith('/oauth/');
  const isNoWrapper = noWrapperRoutes.includes(location.pathname) || isOAuthCallback;

  // Idea detail pages manage their own sidebar — skip the app sidebar
  const isIdeaDetail = isAuthenticated() && /^\/ideas\/[^/]/.test(location.pathname);

  if (isNoWrapper) {
    return (
      <>
        <SystemBanner />
        {children}
        <NotificationContainer />
      </>
    );
  }

  // Idea detail: full-bleed canvas, page owns its own nav
  if (isIdeaDetail) {
    return (
      <div style={{ minHeight: '100vh', background: T.paper, fontFamily: T.sans }}>
        <SystemBanner />
        {children}
        <NotificationContainer />
        <HelpCenterPanel />
      </div>
    );
  }

  // Authenticated users → sidebar shell
  if (isAuthenticated()) {
    return (
      <SidebarLayout user={user} unreadBlogCount={unreadBlogCount}>
        {children}
      </SidebarLayout>
    );
  }

  // Public pages → shared nav
  return (
    <div style={{ minHeight: '100vh', background: T.paper, fontFamily: T.sans }}>
      <PublicNav />
      <SystemBanner />
      <main>{children}</main>
      <NotificationContainer />
    </div>
  );
};

export default Layout;
